import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router';
import { DashboardLayout } from './layouts/DashboardLayout.tsx';
import { useAuthUser } from './lib/use-auth.ts';
import { AccountsPage } from './pages/AccountsPage.tsx';
import { BannerLibraryPage } from './pages/BannerLibraryPage.tsx';
import { CampaignDetailPage } from './pages/campaigns/CampaignDetailPage.tsx';
import { CampaignsListPage } from './pages/campaigns/CampaignsListPage.tsx';
import { ClipsPage } from './pages/ClipsPage.tsx';
import { ClipDetailPage } from './pages/clips/ClipDetailPage.tsx';
import { FilmDetailPage } from './pages/films/FilmDetailPage.tsx';
import { FilmsListPage } from './pages/films/FilmsListPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { OverviewPage } from './pages/OverviewPage.tsx';
import { PostDetailPage } from './pages/PostDetailPage.tsx';
import { PostsPage } from './pages/PostsPage.tsx';
import { QueuePage } from './pages/QueuePage.tsx';
import { AuditPage } from './pages/AuditPage.tsx';
import { UsersPage } from './pages/UsersPage.tsx';
import { JobsPage } from './pages/JobsPage.tsx';
import { VariantsPage } from './pages/variants/VariantsPage.tsx';
import { VariantDetailPage } from './pages/variants/VariantDetailPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactElement }): React.ReactElement {
  const user = useAuthUser();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }): React.ReactElement {
  const user = useAuthUser();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <DashboardLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<OverviewPage />} />
            <Route path="/campaigns" element={<CampaignsListPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/queue" element={<QueuePage />} />
            <Route path="/posts" element={<PostsPage />} />
            <Route path="/posts/:id" element={<PostDetailPage />} />
            <Route path="/films" element={<FilmsListPage />} />
            <Route path="/films/:id" element={<FilmDetailPage />} />
            <Route path="/clips" element={<ClipsPage />} />
            <Route path="/clips/:id" element={<ClipDetailPage />} />
            <Route path="/variants" element={<VariantsPage />} />
            <Route path="/variants/:id" element={<VariantDetailPage />} />
            <Route path="/banners" element={<BannerLibraryPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route
              path="/users"
              element={
                <RequireAdmin>
                  <UsersPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/audit"
              element={
                <RequireAdmin>
                  <AuditPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/jobs"
              element={
                <RequireAdmin>
                  <JobsPage />
                </RequireAdmin>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
